import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Security, LoginCallback, useOktaAuth } from "@okta/okta-react";
import { OktaAuth } from "@okta/okta-auth-js";
import oktaConfig from "./oktaConfig";
import "./App.css";
import ApplePayButton from "./components/ApplePayButton/ApplePayButton";
import GooglePayButton from "./components/GooglePayButton/GooglePayButton";
import SamsungPayButton from "./components/SamsungPayButton/SamsungPayButton";
import WalletTokenOutput from "./components/WalletTokenOutput/WalletTokenOutput";
import SettingsPane from "./components/SettingsPane/SettingsPane";

// Bypass Okta auth when running app from localhost
const isLocal = process.env.NODE_ENV === "development";
const oktaAuth = isLocal
  ? null
  : new OktaAuth({
    issuer: oktaConfig.issuer,
    clientId: oktaConfig.clientId,
    redirectUri: oktaConfig.redirectUri,
  });

function MainApp({ oktaAuth: oktaAuthInstance }) {
  const [tokenOutput, setTokenOutput] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false); // State for info dialog
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 1220);
  const wrapperClass = isWideScreen ? "mainContent" : "screenContent";

  // Handle Pretty Print button animation
  const [isAnimating, setIsAnimating] = useState(false);
  const buttonRef = useRef(null);
  if (buttonRef.current) {
    buttonRef.current.style.setProperty(
      "--animation-play-state",
      isAnimating ? "running" : "paused"
    );
  }

  const [applePaySettings, setApplePaySettings] = useState({
    merchantId: "merchant.wallets.test.suite",
    initiativeContext: "wallets-test-suite.cko.solutions",
    country: "GB",
    mcc: "5691", // Men's and Women's Clothing Stores
    currency: "GBP",
    amount: 12.34,
    paymentType: "final",
    tokenNotificationURL: "https://example.com/applepay/token-notifications",
    managementURL: "https://example.com/applepay/manage",
    multiToken: false,
    multiTokenContexts: [
      { merchantId: "merchant.wallets.test.suite", amount: "6.17" },
      { merchantId: "merchant.wallets.test.suite", amount: "6.17" },
    ],
    allowVisa: true,
    allowMastercard: true,
    allowMaestro: true,
    allowAmex: true,
    allowDiscover: false,
    allowJcb: false,
    allowUnionPay: false,
    allowCb: false,
    allowMada: false,
    allowJaywan: false,
    allowEftpos: false,
    allowDebit: true,
    allowCredit: true,
    requireBillingContact: false,
    requireShippingContact: false,
    requireShippingMethod: false,
    buttonType: "plain",
    buttonStyle: "black",
    buttonLanguage: "en-GB",
  });

  const [googlePaySettings, setGooglePaySettings] = useState({
    merchantId: "BCR2DN5TR6J6PND4",
    gatewayMerchantId: "pk_sbox_adduw2kq4kycw6pfpuduuper7ui",
    country: "GB",
    currency: "GBP",
    amount: 12.34,
    paymentType: "transaction",
    tokenUpdateUrl: "https://example.com/googlepay/token-notifications",
    managementUrl: "https://example.com/googlepay/manage",
    allowVisa: true,
    allowMastercard: true,
    allowMaestro: false,
    // allowElo: true,
    allowAmex: true,
    allowDiscover: false,
    allowJcb: false,
    allowCryptogram3ds: true,
    allowPanOnly: true,
    allowPrepaid: true,
    allowCredit: true,
    requireBillingContact: false,
    requireShippingContact: false,
    requireShippingMethod: false,
    buttonType: "plain",
    buttonStyle: "default",
    buttonLanguage: "en",
  });

  const [samsungPaySettings, setSamsungPaySettings] = useState({
    serviceId: "ddb56eaddd3e47f6b83b10",
    merchantId: "208891814888866325",
    merchantName: "Demo Merchant",
    environment: "STAGE",
    protocol: "PROTOCOL_3DS",
    country: "GB",
    currency: "GBP",
    amount: 12.34,
    paymentType: "PURCHASE",
    allowVisa: true,
    allowMastercard: true,
    allowAmex: false,
    allowDiscover: false,
    allowMada: false,
    buttonStyle: "black",
  });

  const updateScreenWidth = () => {
    setIsWideScreen(window.innerWidth > 1220);
  };

  useEffect(() => {
    window.addEventListener("resize", updateScreenWidth);
    return () => window.removeEventListener("resize", updateScreenWidth);
  }, []);

  const handleOpenSettings = () => setShowSettings(true);
  const handleCloseSettings = () => setShowSettings(false);
  const handleOpenInfoDialog = () => setShowInfoDialog(true);
  const handleCloseInfoDialog = () => setShowInfoDialog(false);

  const handleApplePaySettingsChange = (newSettings) =>
    setApplePaySettings(newSettings);
  const handleGooglePaySettingsChange = (newSettings) =>
    setGooglePaySettings(newSettings);
  const handleSamsungPaySettingsChange = (newSettings) =>
    setSamsungPaySettings(newSettings);

  function handleTokenOutput(authEvent, walletType) {
    let paymentToken;
    if (walletType === "Apple Pay") {
      paymentToken = authEvent.payment;
    } else if (walletType === "Apple Pay Disbursement") {
      paymentToken = authEvent.details;
    } else if (walletType === "Google Pay") {
      // Parse the JSON string token for easier processing
      const parsedToken = JSON.parse(
        authEvent.paymentMethodData.tokenizationData.token
      );
      authEvent.paymentMethodData.tokenizationData.token = parsedToken;
      paymentToken = authEvent.paymentMethodData;
    } else if (walletType === "Samsung Pay") {
      paymentToken = authEvent;
    }
    setIsAnimating(true);
    setTokenOutput(JSON.stringify(paymentToken));
  }

  function prettyPrint() {
    try {
      const obj = JSON.parse(tokenOutput);
      const pretty = JSON.stringify(obj, null, 4);
      setIsAnimating(false);
      setTokenOutput(pretty); // Push the pretty output to state
    } catch (error) {
      console.error("Invalid JSON string:", error);
    }
  }

  return (
    <div className="App">
      <header className="App-header">
        <a
          href="https://forms.gle/9fREb4LsuqPfR3Hc8"
          target="_blank"
          rel="noreferrer"
        >
          <button
            title="Report a bug / Request a feature"
            className="feedback-button"
          >
            🐛
          </button>
        </a>
        <button
          title="Info"
          className="info-button"
          onClick={handleOpenInfoDialog}
        >
          ℹ️
        </button>

        {!isWideScreen && (
          <button onClick={handleOpenSettings} className="settings-button">
            Open Settings <span className="cog">⚙️</span>
          </button>
        )}

        {/* Conditionally render SettingsPane */}
        {isWideScreen ? (
          <SettingsPane
            isSidebar={true}
            applePaySettings={applePaySettings}
            onApplePaySettingsChange={handleApplePaySettingsChange}
            googlePaySettings={googlePaySettings}
            onGooglePaySettingsChange={handleGooglePaySettingsChange}
            samsungPaySettings={samsungPaySettings}
            onSamsungPaySettingsChange={handleSamsungPaySettingsChange}
          />
        ) : (
          showSettings && (
            <SettingsPane
              isSidebar={false}
              onClose={handleCloseSettings}
              applePaySettings={applePaySettings}
              onApplePaySettingsChange={handleApplePaySettingsChange}
              googlePaySettings={googlePaySettings}
              onGooglePaySettingsChange={handleGooglePaySettingsChange}
              samsungPaySettings={samsungPaySettings}
              onSamsungPaySettingsChange={handleSamsungPaySettingsChange}
            />
          )
        )}

        {oktaAuthInstance && (
          <button
            title="Sign out"
            onClick={() => oktaAuthInstance.signOut()}
            className="sign-out-button"
          >
            <span className="full-logout">🚪⬅</span>
            <span className="short-logout">🚪</span>
          </button>
        )}

        {showInfoDialog && (
          <div className="info-dialog">
            <div className="info-dialog-content animation">
              <div className="top-bar">
                <div className="info-button">
                  <button onClick={handleCloseInfoDialog}>×</button>
                </div>
                <h3>Where do I start?</h3>
              </div>
              <p>
                Test with a real wallet, or use the following test cards in
                Sandbox:
              </p>
              <ul>
                <li className="apple">
                  <a
                    href="https://developer.apple.com/apple-pay/sandbox-testing/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Apple Pay
                  </a>
                </li>
                <li className="google">
                  <a
                    href="https://developers.google.com/pay/api/web/guides/resources/test-card-suite"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Google Pay
                  </a>
                </li>
                <li className="samsung">
                  <a
                    href="https://developer.samsung.com/pay/web-sdk/test-cards.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Samsung Pay
                  </a>
                </li>
              </ul>
            </div>
          </div>
        )}
        <main>
          <div className={wrapperClass}>
            <ApplePayButton
              tokenOutput={tokenOutput}
              handleTokenOutput={handleTokenOutput}
              applePaySettings={applePaySettings}
            />
            <GooglePayButton
              tokenOutput={tokenOutput}
              handleTokenOutput={handleTokenOutput}
              googlePaySettings={googlePaySettings}
            />
            <SamsungPayButton
              tokenOutput={tokenOutput}
              handleTokenOutput={handleTokenOutput}
              samsungPaySettings={samsungPaySettings}
            />
            <WalletTokenOutput
              tokenOutput={tokenOutput}
              prettyPrint={prettyPrint}
              isAnimating={isAnimating}
              applePaySettings={applePaySettings}
              googlePaySettings={googlePaySettings}
            />
          </div>
        </main>
      </header>
    </div>
  );
}

function AuthenticatedApp() {
  const { authState, oktaAuth: oktaAuthInstance } = useOktaAuth();

  if (!authState) {
    // Show a loading screen until authState is defined
    return <div>Loading...</div>;
  }

  if (!authState.isAuthenticated) {
    oktaAuthInstance.signInWithRedirect();
    return <div>Redirecting to login...</div>;
  }

  return <MainApp oktaAuth={oktaAuthInstance} />;
}

function AppWithSecurity() {
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    window.location.replace(originalUri || "/");
  };

  return (
    <Router>
      {isLocal ? (
        <Routes>
          <Route path="/*" element={<MainApp oktaAuth={null} />} />
        </Routes>
      ) : (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <Routes>
            <Route path="/login/callback" element={<LoginCallback />} />
            <Route path="/*" element={<AuthenticatedApp />} />
          </Routes>
        </Security>
      )}
    </Router>
  );
}

export default AppWithSecurity;
